import '../../App.scss';
import { sanitizeHtml } from '../../util/sanitizeHtml';
import { Route, Routes, useNavigate, useLocation } from "react-router-dom"
import { getTranslatedText } from "../../util/util"
import usePageTranslation from "../../hooks/ui/usePageTranslation";
import { Suspense, useEffect, useRef } from "react";
import React from "react";
import KioskHandler from "./KioskHandler";
import { useGetCompanyThemeDetailsMutation } from "../../api-integration/secure/api.kiosk";
import {useGetSessionStatusQuery} from "../../api-integration/secure/api.account";
import { normalizeAPIResponse } from "../../util/normalizeAPIResponse";
import { useTheme } from "../../theme/ThemeContext";
import { updateAlertMessage, selectIsSessionExpired, selectReloadPageAfterSessionExpired, selectIsRefreshTokenExpired, selectFullPageLoader } from "../../api-integration/common.slice";
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import KioskNonAuthGuard from "../../guards/kiosk-non-auth.guard";
import KioskAuthGuard from "../../guards/kiosk-auth.guard";
import { useExtendSessionMutation } from "../../api-integration/public/common";
import {setAuthChecking, setKioskSession, clearKioskSession, selectKioskIsAuthChecking, updateKioskFlags} from "../../api-integration/kioskAuthSlice";
import { Alert } from '../../components/common/alert/alert';
import { classifyError } from '../../util/telemetry/error/error-classifier.util';
import { InitializationErrorScreen } from '../../components/common/error/initialize-error-screen';
import ErrorBoundary from '../../components/common/error/error-boundary';
import { useState } from 'react';


const KioskLogin = React.lazy(() => import("../pages/public/kiosk-login"));
const KioskConsent = React.lazy(() => import("../pages/public/kiosk-consent"));
const KioskCodeVerify = React.lazy(() => import("../pages/public/kiosk-code-verify"));
const KioskInvalidInvite = React.lazy(() => import("../pages/public/kiosk-invalid-invite"));
const KioskHome = React.lazy(() => import("../pages/secure/kiosk-home"));
const KioskAction = React.lazy(() => import("../pages/secure/kiosk-action"));
const KioskHistory = React.lazy(() => import("../pages/secure/kiosk-history"));
const KioskProfile = React.lazy(() => import("../pages/secure/kiosk-profile"));
const KioskLogout = React.lazy(() => import("../pages/secure/kiosk-logout"));
const KioskExternalGateway = React.lazy(() => import("../pages/public/kiosk-external-gateway"));



const KioskRoutes = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const {setTheme} = useTheme();
    const PageTranslation = usePageTranslation();

    const isAuthChecking = useAppSelector(selectKioskIsAuthChecking);
    const isSessionExpired = useAppSelector(selectIsSessionExpired);
    const reloadPageAfterSessionExpired = useAppSelector(selectReloadPageAfterSessionExpired);
    const isRefreshTokenExpired = useAppSelector(selectIsRefreshTokenExpired);
    const fullPageLoaderSelector = useAppSelector(selectFullPageLoader);

    const [getCompanyThemeDetailsAPI, getCompanyThemeDetailsResult] = useGetCompanyThemeDetailsMutation();

    const [extendSesionAPI, { data: extendSessionResponse, isSuccess: isSessionExtendedSuccess, isError: isErrorWhileExtendingSession }] 
    = useExtendSessionMutation();

    const sessionExtendAttemptedRef = useRef(false);
    const [initErrorClassification, setInitErrorClassification] = useState<'NETWORK_ERROR' | 'SERVER_ERROR' | 'UNKNOWN_ERROR' | null>(null);

    useEffect(() => {
        if (isSessionExtendedSuccess) {
          if (extendSessionResponse?.success === true) {
            if (reloadPageAfterSessionExpired) {
              window.location.reload();
            }
          } else {
            dispatch(updateAlertMessage({ status: 'success', message: "Session could not be extended" }));
            dispatch(clearKioskSession());
          }
        }
        else if (isErrorWhileExtendingSession) {
            dispatch(updateAlertMessage({ status: 'success', message: "Session could not be extended" }));
            dispatch(clearKioskSession());
        }
    }, [extendSessionResponse, isSessionExtendedSuccess, isErrorWhileExtendingSession]);

    useEffect(() => {
        if (isRefreshTokenExpired) {
            dispatch(clearKioskSession());
        }
    }, [isRefreshTokenExpired])

    useEffect(() => {
        if (isSessionExpired === true && !sessionExtendAttemptedRef.current) {
          sessionExtendAttemptedRef.current = true;
          extendSesionAPI({});
        }
      }, [isSessionExpired]);

    const isExternalRoute = location.pathname.toLowerCase().startsWith('/external');
    const isLogoutRoute = location.pathname.toLowerCase().endsWith('/logout');

    useEffect(() => {
        if (isLogoutRoute) {
            dispatch(clearKioskSession());
        }
    }, [isLogoutRoute, dispatch]);

    useEffect(() => {
        getCompanyThemeDetailsAPI({});
        sessionStorage.removeItem('chunk_reload_attempted');
    },[]);

    useEffect(() => {
        if(getCompanyThemeDetailsResult?.isSuccess || getCompanyThemeDetailsResult?.isError) {
            const response = normalizeAPIResponse(getCompanyThemeDetailsResult);
            if(response?.success === true) {
                const companyThemeData = response?.data?.companyThemeDetails || {};
                const themeDetails = companyThemeData?.themeDetails || {};
                const companyDetails = companyThemeData?.companyDetails || {};
                localStorage.setItem("kioskCompanyDetails", JSON.stringify(companyDetails));
                setTheme(themeDetails);
            }
            else {
                dispatch(updateAlertMessage({status: 'error', message: response.message}));
                navigate('/login');
            }
        }
    },[getCompanyThemeDetailsResult?.isSuccess, getCompanyThemeDetailsResult?.isError]);

    const getSessionStatusResult = useGetSessionStatusQuery(undefined, {
        skip: isExternalRoute || isLogoutRoute,
    });

    useEffect(() => {
        if(!isExternalRoute) {
            dispatch(setAuthChecking(true));
        }
        else {
            dispatch(setAuthChecking(false));
        }
    }, [isExternalRoute, dispatch]);

    useEffect(() => {
        if(getSessionStatusResult.isSuccess || getSessionStatusResult.isError) {
            const response = normalizeAPIResponse(getSessionStatusResult);
            if(response?.success === true && response?.data?.user) {
                const userData = response.data.user;
                dispatch(
                    setKioskSession({
                        user: {
                            userEmail: userData.userEmail,
                            userBusinessEmail: userData.userBusinessEmail,
                            userId: userData.userId,
                            firstName: userData.firstName,
                            lastName: userData.lastName,
                            picPath: userData.picPath,
                        }
                    })
                );
                dispatch(
                    updateKioskFlags({
                        isConsentProvided: userData.isConsentProvided ?? false,
                        isPhoneVerified: userData.isPhoneVerified ?? false,
                    })
                );
                setInitErrorClassification(null);
            }
            else {
                if (getSessionStatusResult.isError) {
                    const classification = classifyError(getSessionStatusResult.error);
                    if (classification === 'NETWORK_ERROR' || classification === 'SERVER_ERROR' || classification === 'UNKNOWN_ERROR') {
                        setInitErrorClassification(classification);
                        dispatch(setAuthChecking(false));
                        return;
                    }
                }
                dispatch(clearKioskSession());
            }
            dispatch(setAuthChecking(false));
        }
    },[getSessionStatusResult?.isSuccess, getSessionStatusResult?.isError]);

    if (initErrorClassification) {
        return (
            <InitializationErrorScreen 
                classification={initErrorClassification} 
            />
        );
    }

    if (isAuthChecking) {
        return (
            <div className='fullpage-loader'>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <Suspense fallback={<div dangerouslySetInnerHTML={{ __html: sanitizeHtml(getTranslatedText('loading.message', PageTranslation)) }}></div>}>
            <ErrorBoundary>
            <Routes>
                <Route path="/" element={<KioskNonAuthGuard><KioskLogin /></KioskNonAuthGuard>} />
                <Route path="/invite" element={<KioskHandler />} />
                <Route path="/login" element={<KioskNonAuthGuard><KioskLogin /></KioskNonAuthGuard>} />
                <Route path="/consent" element={<KioskConsent />} />
                <Route path="/verify" element={<KioskAuthGuard><KioskCodeVerify /></KioskAuthGuard>} />
                <Route path="/invalid-invite" element={<KioskInvalidInvite />} />
                <Route path="/external/:id" element={<KioskNonAuthGuard><KioskExternalGateway /></KioskNonAuthGuard>} ></Route>
                <Route path="/home" element={<KioskAuthGuard><KioskHome /></KioskAuthGuard>} />
                <Route path="/action" element={<KioskAuthGuard><KioskAction /></KioskAuthGuard>} />
                <Route path="/history" element={<KioskAuthGuard><KioskHistory /></KioskAuthGuard>} />
                <Route path="/profile" element={<KioskAuthGuard><KioskProfile /></KioskAuthGuard>} />
                <Route path="/logout" element={<KioskLogout />} />
                <Route path="*" element={<KioskInvalidInvite />} />
            </Routes>
            </ErrorBoundary>
            </Suspense>
            {fullPageLoaderSelector && 
            <div className='fullpage-loader' >
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                    Loading…
                    </span>
                </div>
                </div>
            }
            <Alert />
        </>
    )
}
export default KioskRoutes;